import {
  FontPicker,
  GroupedComboBoxOnRenderMenuProps,
  GroupedComboBoxOnRenderTooltipProps,
  IFont,
  IFontFamily,
  IMruEntry,
} from 'modern-font-picker';
import * as React from 'react';

import { DirectionalHint, getId, ITheme } from '@fluentui/react';

import { dataUniqueId, getInputMethodFromEvent, InputMethod } from '@1js/acui-common';
import { AppContext } from '@1js/acui-context';
import { getDownChevronIcon, iconNameToIconProps } from '@1js/acui-icons';
import { AppLabel } from '@1js/acui-label';
import { AppThemeConsumer } from '@1js/acui-theme';
import { AppTooltipHostWrapper, AppTooltipManager, useAppTooltipHost } from '@1js/acui-tooltip';

import { AppFontInfoRequestTrigger, AppFontPickerInternalProps } from './AppFontPicker.Props';
import { getLabelStyle, getStyleFunction, labelContainerStyles } from './AppFontPicker.styles';
import { defaultRenderEmptyContent } from './defaultRenderEmptyContent';

function onRenderMenu(
  props: GroupedComboBoxOnRenderMenuProps,
  defaultRender: (props: GroupedComboBoxOnRenderMenuProps) => JSX.Element | null
) {
  // Wrap the menu inside AppTooltipManager so that all tooltips in this menu
  // will render on a separate layer, on top of the menu callout.
  return <AppTooltipManager>{defaultRender(props)}</AppTooltipManager>;
}

const RenderTooltipHost = (props: GroupedComboBoxOnRenderTooltipProps) => {
  const { tooltip, showTooltipOnRight, mainContent, hoverElement, hostClassName, targetElement } = props;

  const { setTooltipElement, updateTooltipsHidden } = React.useContext(AppContext);

  return (
    <AppTooltipHostWrapper
      tooltipTargetRef={targetElement}
      tooltipLabel={tooltip}
      tooltipId={getId('AppFontPickerTooltip')}
      hoverElement={hoverElement}
      containerClassname={hostClassName}
      tooltipDirection={showTooltipOnRight ? DirectionalHint.rightCenter : DirectionalHint.leftCenter}
      tooltipDirectionForRTL={showTooltipOnRight ? DirectionalHint.leftCenter : DirectionalHint.rightCenter}
      setTooltipElement={setTooltipElement}
      updateTooltipsHidden={updateTooltipsHidden}
      isMenuComponent={true}
    >
      {mainContent}
    </AppTooltipHostWrapper>
  );
};

export const AppFontPicker: React.FunctionComponent<AppFontPickerInternalProps> = props => {
  const {
    componentRef,
    fonts,
    mruList,
    initialPinnedFontKeys,
    selectedFont,
    disabled,
    maximumMruEntries = 10,
    previewFontFamily,
    hideFocusVisual = true,
    onExecute,
    onFontPinChanged,
    onFontInfoRequested,
    isSubscriber,
    stringResources,
    iconResources,
    menuInitiallyOpened,
    id,
    styles,
    width,
    dropDownWidth,
    label,
    labelStyles,
    showLabel,
    keytipProps,
    onMenuOpen,
    onAfterMenuDismiss,
    location,
    downChevronIcon,
    downChevronIconSize,
    scrollbarWidth,
    dataAutomationType,
    directionalHintDynamic,
    icon,
    iconColor,
    hasPinnedControl,
    maxTextLength,
    allowFontInfoPane,
    allowPinning,
    itemHeight,
  } = props;

  const fontPickerId = dataUniqueId(props) || id;

  const { updateTooltipsHidden } = React.useContext(AppContext);

  const onMenuOpenCallback = React.useCallback(() => {
    updateTooltipsHidden?.(true);
    onMenuOpen?.({ id: fontPickerId, location });
  }, [fontPickerId, location, onMenuOpen, updateTooltipsHidden]);

  const onMenuDismissedCallback = React.useCallback(() => {
    updateTooltipsHidden?.(false);
    onAfterMenuDismiss?.(fontPickerId);
  }, [onAfterMenuDismiss, fontPickerId, updateTooltipsHidden]);

  const onFontSelectedCallback = React.useCallback(
    (font: IFont | string, ev?: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>) => {
      onExecute?.({
        selectedFont: font,
        id: fontPickerId,
        location,
        shouldTakeFocus: !(ev && 'key' in ev && ev.key === 'Tab'),
        inputMethod: getInputMethodFromEvent(ev),
      });
    },
    [onExecute, fontPickerId, location]
  );

  const onFontChangeCallback = React.useCallback(
    (font: IFont | string, keyCode?: string) => {
      onExecute?.({
        selectedFont: font,
        id: fontPickerId,
        location,
        shouldTakeFocus: keyCode !== 'Tab',
        inputMethod: keyCode ? InputMethod.Keyboard : undefined,
      });
    },
    [onExecute, fontPickerId, location]
  );

  const onFontInfoRequestedCallback = React.useCallback(
    (
      font: IFontFamily,
      trigger: AppFontInfoRequestTrigger,
      ev?: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>
    ) => {
      onFontInfoRequested?.({
        id: fontPickerId,
        font,
        location,
        trigger,
        inputMethod: getInputMethodFromEvent(ev),
      });
    },
    [onFontInfoRequested, fontPickerId, location]
  );

  const onFontPinnedCallback = React.useCallback(
    (font: IFontFamily, ev?: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>) => {
      onFontPinChanged?.({
        id: fontPickerId,
        font,
        location,
        isPinned: true,
        inputMethod: getInputMethodFromEvent(ev),
      });
    },
    [onFontPinChanged, fontPickerId, location]
  );

  const onFontUnpinnedCallback = React.useCallback(
    (font: IFontFamily, ev?: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>) => {
      onFontPinChanged?.({
        id: fontPickerId,
        font,
        location,
        isPinned: false,
        inputMethod: getInputMethodFromEvent(ev),
      });
    },
    [onFontPinChanged, fontPickerId, location]
  );

  const { tooltipRef } = useAppTooltipHost<HTMLDivElement>(`${fontPickerId}-tooltip`, {
    label,
    direction: DirectionalHint.bottomCenter,
  });

  const emptySearchResultLabel = stringResources?.emptySearchResultLabel;

  const renderEmptyContentCallback = React.useCallback(
    (filterText?: string) => defaultRenderEmptyContent(emptySearchResultLabel, filterText),
    [emptySearchResultLabel]
  );

  const updatedIconResources = React.useMemo(
    () => ({ ...iconResources, dropDownButtonName: getDownChevronIcon(props) }),
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- getDownChevronIcons() only use the downChevronIcon and downChevronIconSize fields. */
    [iconResources, downChevronIcon, downChevronIconSize]
  );

  const onFontPreviewCallback = React.useCallback(
    (font: IFont): string | undefined => {
      const themeStyle =
        (font as IMruEntry)?.themeStyle || mruList?.find(item => item.mruKey === font.name)?.themeStyle;
      return themeStyle ? `${font.name} (${themeStyle})` : font.name;
    },
    [mruList]
  );

  const styleFunction = getStyleFunction(styles, hasPinnedControl);

  const fontPicker = (theme: ITheme) => (
    <FontPicker
      id={fontPickerId}
      data-unique-id={dataUniqueId(props)}
      data-automation-type={dataAutomationType ?? 'AppFontPicker'}
      ref={tooltipRef}
      componentRef={componentRef}
      fonts={fonts}
      mruList={mruList}
      initialPinnedFontKeys={initialPinnedFontKeys}
      selectedFont={selectedFont}
      disabled={disabled}
      maximumMruEntries={maximumMruEntries}
      previewFontFamily={previewFontFamily}
      hideFocusVisual={hideFocusVisual}
      onFontChange={onFontChangeCallback}
      onFontSelected={onFontSelectedCallback}
      onFontPinned={onFontPinnedCallback}
      onFontUnpinned={onFontUnpinnedCallback}
      onFontInfoRequested={onFontInfoRequestedCallback}
      onFontPreview={onFontPreviewCallback}
      isSubscriber={isSubscriber}
      stringResources={stringResources}
      iconResources={updatedIconResources}
      onMenuDismissed={onMenuDismissedCallback}
      onMenuOpened={onMenuOpenCallback}
      ariaLabel={label}
      menuInitiallyOpened={menuInitiallyOpened}
      renderEmptyContent={renderEmptyContentCallback}
      styles={styleFunction}
      theme={theme}
      width={width}
      onRenderTooltip={RenderTooltipHost}
      dropDownWidth={dropDownWidth}
      keytipProps={keytipProps}
      scrollbarWidth={scrollbarWidth}
      onRenderMenu={onRenderMenu}
      directionalHintFixed={!directionalHintDynamic}
      maxTextLength={maxTextLength}
      allowFontInfoPane={allowFontInfoPane}
      allowPinning={allowPinning}
      itemHeight={itemHeight}
    />
  );

  const showIcon: boolean = !!icon;
  if (showLabel || showIcon) {
    return (
      <AppThemeConsumer>
        {theme => (
          <div style={labelContainerStyles}>
            <AppLabel
              type="AppLabelProps"
              iconProps={showIcon ? iconNameToIconProps(icon, iconColor, undefined, undefined, disabled) : undefined}
              forId={fontPickerId}
              id={`${fontPickerId}-label`}
              disabled={disabled}
              label={label}
              styles={getLabelStyle(theme, disabled, labelStyles)}
            />

            {fontPicker(theme)}
          </div>
        )}
      </AppThemeConsumer>
    );
  }

  return <AppThemeConsumer>{fontPicker}</AppThemeConsumer>;
};

AppFontPicker.displayName = 'AppFontPicker';
